import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { TenantDetail } from '@order-pilot/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SelectField, TextAreaField, TextField } from '@/components/Form';
import { IconArrowLeft } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import { ErrorState, Skeleton } from '@/components/States';
import { fetchTenant, tenantKeys, updateTenant } from '@/features/tenants/api';
import { fetchPlans, planKeys } from '@/features/plans/api';
import type { UpdateTenantInput } from '@/features/tenants/api';
import { getErrorMessage } from '@/lib/api';
import { IANA_TIMEZONES } from '@/lib/timezones';
import { compactErrors, requiredText } from '@/lib/validate';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'MYR', 'SGD'] as const;

interface FormState {
  name: string;
  phone: string;
  address: string;
  timezone: string;
  currency: string;
  /** '' means no plan — the same "deliberately unassigned" state the API uses. */
  planId: string;
}

type FormField = keyof FormState;

function toFormState(tenant: TenantDetail): FormState {
  return {
    name: tenant.name,
    phone: tenant.phone ?? '',
    address: tenant.address ?? '',
    timezone: tenant.timezone,
    currency: tenant.currency,
    planId: tenant.plan?.id ?? '',
  };
}

function BackLink({ tenantId }: { tenantId: string }) {
  return (
    <Link
      to={`/cp/tenants/${tenantId}`}
      className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-slate-500 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
    >
      <IconArrowLeft className="h-4 w-4" />
      {tenantId ? 'Tenant' : 'Tenants'}
    </Link>
  );
}

/**
 * A full page rather than a modal — editing a tenant is a deliberate,
 * navigable action (deep-linkable, survives a reload), not a quick aside on
 * top of the detail page. Deliberately has NO logo control: logo add/replace/
 * remove lives as its own action on the detail page, not bundled in here —
 * same reasoning as pulling status out into its own confirmed action.
 */
export function EditTenantPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const plansQuery = useQuery({
    queryKey: planKeys.list(),
    queryFn: ({ signal }) => fetchPlans(signal),
  });
  const [form, setForm] = useState<FormState | null>(null);
  const [errors, setErrors] = useState<Partial<Record<FormField, string>>>({});

  const tenantQuery = useQuery({
    queryKey: tenantKeys.detail(id),
    queryFn: ({ signal }) => fetchTenant(id, signal),
    enabled: id !== '',
  });

  // Seeds the form once the tenant loads. Not re-synced on every refetch —
  // once the SA starts typing, a background refresh should never clobber it.
  useEffect(() => {
    if (tenantQuery.data && form === null) {
      setForm(toFormState(tenantQuery.data));
    }
  }, [tenantQuery.data, form]);

  const mutation = useMutation({
    mutationFn: (input: UpdateTenantInput) => updateTenant(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.all });
      navigate(`/cp/tenants/${id}`);
    },
  });

  const update = useCallback((patch: Partial<FormState>) => {
    setForm((current) => (current ? { ...current, ...patch } : current));
    setErrors((current) => {
      const touched = Object.keys(patch) as FormField[];
      if (!touched.some((field) => current[field])) return current;
      const next = { ...current };
      for (const field of touched) delete next[field];
      return next;
    });
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!form) return;

      const validation = compactErrors<FormField>({
        name: requiredText(form.name, 'Restaurant name'),
        timezone: requiredText(form.timezone, 'Timezone'),
        currency: requiredText(form.currency, 'Currency'),
      });
      setErrors(validation);
      if (Object.keys(validation).length > 0) return;

      mutation.mutate({
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        timezone: form.timezone.trim(),
        currency: form.currency.trim().toUpperCase(),
        // '' clears the plan; an explicit null is what the API reads as
        // "unassign", distinct from omitting the key.
        planId: form.planId === '' ? null : form.planId,
      });
    },
    [form, mutation],
  );

  if (tenantQuery.isPending) {
    return (
      <>
        <PageHeader eyebrow={<BackLink tenantId={id} />} title="Loading…" />
        <div className="card max-w-3xl p-5">
          <Skeleton className="h-4 w-48" />
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="space-y-2">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-8 w-full" />
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  if (tenantQuery.isError || !tenantQuery.data || !form) {
    return (
      <>
        <PageHeader eyebrow={<BackLink tenantId={id} />} title="Edit tenant" />
        <div className="card">
          <ErrorState
            error={tenantQuery.error}
            title="Could not load this tenant"
            onRetry={() => void tenantQuery.refetch()}
            retrying={tenantQuery.isFetching}
          />
        </div>
      </>
    );
  }

  const tenant = tenantQuery.data;
  const currencyOptions = CURRENCIES.includes(form.currency as (typeof CURRENCIES)[number])
    ? CURRENCIES
    : [form.currency, ...CURRENCIES];

  return (
    <>
      <PageHeader eyebrow={<BackLink tenantId={id} />} title={`Edit ${tenant.name}`} />

      <form onSubmit={handleSubmit} noValidate className="max-w-3xl space-y-6">
        {mutation.isError ? (
          <Alert tone="error" title="Could not save changes">
            {getErrorMessage(mutation.error)}
          </Alert>
        ) : null}

        <section className="card" aria-labelledby="section-restaurant">
          <div className="card-header">
            <div>
              <h2 id="section-restaurant" className="text-sm font-semibold text-slate-900">
                Restaurant
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Changes apply to the portal and to how POS dates are interpreted.
              </p>
            </div>
          </div>
          <div className="grid gap-5 px-5 py-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <TextField
                id="name"
                label="Restaurant name"
                required
                autoFocus
                autoComplete="organization"
                value={form.name}
                onChange={(event) => update({ name: event.target.value })}
                error={errors.name}
                disabled={mutation.isPending}
              />
            </div>
            <TextField
              id="phone"
              label="Phone"
              type="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={(event) => update({ phone: event.target.value })}
              disabled={mutation.isPending}
            />
            <SelectField
              id="timezone"
              label="Timezone"
              required
              hint="IANA timezone name."
              value={form.timezone}
              onChange={(event) => update({ timezone: event.target.value })}
              error={errors.timezone}
              disabled={mutation.isPending}
            >
              {IANA_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </SelectField>
            <SelectField
              id="planId"
              label="Plan"
              hint="Limits warn, they never block. Clearing this leaves the tenant unassigned."
              value={form.planId}
              onChange={(event) => update({ planId: event.target.value })}
              disabled={mutation.isPending || plansQuery.isPending}
            >
              <option value="">No plan</option>
              {(plansQuery.data ?? []).map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              id="currency"
              label="Currency"
              required
              value={form.currency}
              onChange={(event) => update({ currency: event.target.value })}
              error={errors.currency}
              disabled={mutation.isPending}
            >
              {currencyOptions.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </SelectField>
            <div className="sm:col-span-2">
              <TextAreaField
                id="address"
                label="Address"
                value={form.address}
                onChange={(event) => update({ address: event.target.value })}
                disabled={mutation.isPending}
              />
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={() => navigate(`/cp/tenants/${id}`)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </form>
    </>
  );
}
